/**
 * Character sprites, drawn rather than loaded.
 *
 * Each character is assembled from a 16x20 grid of coloured cells — the same
 * approach as the tile renderer, and for the same reason: no art pipeline, no
 * spritesheet to keep in sync with the code, and a new hairstyle is a few
 * lines rather than a file.
 *
 * Drawing pixel-by-pixel for sixty characters at 60fps would be far too slow,
 * so each (appearance, facing, frame) is rendered once into a small offscreen
 * canvas and then blitted. The number of distinct combinations is small and
 * bounded, which is what makes the cache work.
 */

import {
  CLOTHING_COLORS,
  HAIR_COLORS,
  HAIR_STYLES,
  SKIN_TONES,
  SPRITE_HEIGHT,
  SPRITE_SCALE,
  SPRITE_WIDTH,
  type Appearance,
  type Facing,
} from '@virtual-office/shared';

/** Walk cycle: two contact poses, with the standing pose between them. */
export type Frame = 0 | 1 | 2;

const pick = <T>(list: readonly T[], index: number): T =>
  list[((index % list.length) + list.length) % list.length] as T;

/* -------------------------------------------------------------------------- */
/* Shape helpers                                                               */
/* -------------------------------------------------------------------------- */

type Ctx = CanvasRenderingContext2D;

/** One logical pixel. All shapes are built from these. */
const px = (ctx: Ctx, x: number, y: number, w = 1, h = 1): void => {
  ctx.fillRect(x, y, w, h);
};

/**
 * Hair, by style. Drawn in two passes — behind the head and over the top —
 * so longer styles frame the face instead of covering it.
 */
function drawHair(ctx: Ctx, style: string, facing: Facing, layer: 'back' | 'front'): void {
  const back = facing === 'up';

  if (style === 'bald') return;

  if (layer === 'back') {
    switch (style) {
      case 'long':
        px(ctx, 3, 4, 10, 9);
        break;
      case 'bob':
        px(ctx, 3, 4, 10, 6);
        break;
      case 'ponytail':
        px(ctx, 4, 3, 8, 4);
        // The tail sits behind whichever way they face.
        if (facing === 'left') px(ctx, 11, 5, 2, 5);
        else if (facing === 'right') px(ctx, 3, 5, 2, 5);
        else px(ctx, 7, 8, 2, 5);
        break;
      default:
        break;
    }
    return;
  }

  // Front layer: the crown, common to every style that has one.
  switch (style) {
    case 'buzz':
      px(ctx, 4, 3, 8, 2);
      break;
    case 'short':
      px(ctx, 4, 2, 8, 3);
      if (!back) px(ctx, 4, 5, 2, 1);
      break;
    case 'messy':
      px(ctx, 4, 2, 8, 3);
      px(ctx, 3, 3, 1, 2);
      px(ctx, 12, 3, 1, 2);
      px(ctx, 5, 1, 2, 1);
      px(ctx, 9, 1, 3, 1);
      break;
    case 'curly':
      px(ctx, 4, 2, 8, 3);
      px(ctx, 3, 2, 1, 3);
      px(ctx, 12, 2, 1, 3);
      px(ctx, 5, 1, 1, 1);
      px(ctx, 7, 1, 1, 1);
      px(ctx, 10, 1, 1, 1);
      break;
    case 'bob':
    case 'long':
      px(ctx, 4, 2, 8, 3);
      if (!back) {
        px(ctx, 3, 4, 2, 4);
        px(ctx, 11, 4, 2, 4);
      }
      break;
    case 'ponytail':
      px(ctx, 4, 2, 8, 3);
      break;
    default:
      break;
  }
}

/** Legs, offset by walk frame so the character strides rather than slides. */
function drawLegs(ctx: Ctx, frame: Frame, pants: string, shoe: string): void {
  const lift = frame === 1 ? 1 : 0;
  const lift2 = frame === 2 ? 1 : 0;

  ctx.fillStyle = pants;
  px(ctx, 5, 14, 3, 4 - lift);
  px(ctx, 8, 14, 3, 4 - lift2);

  ctx.fillStyle = shoe;
  px(ctx, 5, 18 - lift, 3, 1);
  px(ctx, 8, 18 - lift2, 3, 1);
}

/* -------------------------------------------------------------------------- */
/* The character                                                               */
/* -------------------------------------------------------------------------- */

function paint(ctx: Ctx, look: Appearance, facing: Facing, frame: Frame): void {
  const skin = pick(SKIN_TONES, look.skin);
  const hairColor = pick(HAIR_COLORS, look.hairColor);
  const shirt = pick(CLOTHING_COLORS, look.shirt);
  const pants = pick(CLOTHING_COLORS, look.pants);
  const style = pick(HAIR_STYLES, look.hair);

  // Behind everything: long hair, ponytails.
  ctx.fillStyle = hairColor;
  drawHair(ctx, style, facing, 'back');

  drawLegs(ctx, frame, pants, '#2a2a30');

  // Torso, with arms a shade darker so they read as separate from the shirt.
  ctx.fillStyle = shirt;
  px(ctx, 4, 9, 8, 6);
  ctx.fillStyle = 'rgba(0,0,0,0.18)';
  px(ctx, 4, 9, 1, 5);
  px(ctx, 11, 9, 1, 5);

  // Hands.
  ctx.fillStyle = skin;
  px(ctx, 4, 13, 1, 2);
  px(ctx, 11, 13, 1, 2);

  // Head.
  px(ctx, 4, 3, 8, 7);
  // Neck shadow, so the head sits on the shoulders.
  ctx.fillStyle = 'rgba(0,0,0,0.15)';
  px(ctx, 6, 9, 4, 1);

  // Face, only when we can see it.
  if (facing !== 'up') {
    ctx.fillStyle = '#2a2118';
    if (facing === 'down') {
      px(ctx, 6, 6, 1, 2);
      px(ctx, 9, 6, 1, 2);
      ctx.fillStyle = 'rgba(42,33,24,0.55)';
      px(ctx, 7, 8, 2, 1);
    } else if (facing === 'left') {
      px(ctx, 5, 6, 1, 2);
      ctx.fillStyle = 'rgba(42,33,24,0.55)';
      px(ctx, 5, 8, 2, 1);
    } else {
      px(ctx, 10, 6, 1, 2);
      ctx.fillStyle = 'rgba(42,33,24,0.55)';
      px(ctx, 9, 8, 2, 1);
    }
  }

  // Over the top: the crown and fringe.
  ctx.fillStyle = hairColor;
  drawHair(ctx, style, facing, 'front');
}

/* -------------------------------------------------------------------------- */
/* Cache                                                                       */
/* -------------------------------------------------------------------------- */

const cache = new Map<string, HTMLCanvasElement>();

const keyOf = (look: Appearance, facing: Facing, frame: Frame): string =>
  `${look.skin}.${look.hair}.${look.hairColor}.${look.shirt}.${look.pants}|${facing}|${frame}`;

/**
 * A character, ready to blit. Rendered once per distinct look and pose; an
 * office of sixty people sharing a handful of styles costs a few dozen small
 * canvases.
 */
export function characterSprite(
  look: Appearance,
  facing: Facing,
  frame: Frame,
  scale = SPRITE_SCALE,
): HTMLCanvasElement {
  const key = `${keyOf(look, facing, frame)}@${scale}`;
  const cached = cache.get(key);
  if (cached) return cached;

  const canvas = document.createElement('canvas');
  canvas.width = SPRITE_WIDTH * scale;
  canvas.height = SPRITE_HEIGHT * scale;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    // Crisp edges: this is pixel art, not a photo.
    ctx.imageSmoothingEnabled = false;
    ctx.scale(scale, scale);
    paint(ctx, look, facing, frame);
  }

  // Unbounded growth is not a risk at these numbers, but a stray custom scale
  // could surprise us. Keep a ceiling.
  if (cache.size > 600) cache.clear();
  cache.set(key, canvas);
  return canvas;
}

/** Which walk frame to show, from distance travelled rather than wall time. */
export function walkFrame(distance: number, moving: boolean): Frame {
  if (!moving) return 0;
  // A step every 12 pixels reads as walking at our movement speed.
  const phase = Math.floor(distance / 12) % 4;
  return phase === 1 ? 1 : phase === 3 ? 2 : 0;
}

/** A face-only crop, for the roster and chat when there is no uploaded photo. */
export function portraitSprite(look: Appearance, scale = 4): HTMLCanvasElement {
  const key = `portrait|${keyOf(look, 'down', 0)}@${scale}`;
  const cached = cache.get(key);
  if (cached) return cached;

  const canvas = document.createElement('canvas');
  canvas.width = 12 * scale;
  canvas.height = 12 * scale;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.imageSmoothingEnabled = false;
    ctx.scale(scale, scale);
    // Shift up and left so the head fills the frame.
    ctx.translate(-2, -1);
    paint(ctx, look, 'down', 0);
  }
  cache.set(key, canvas);
  return canvas;
}
